import { useMocks } from './env';

/**
 * EPN-98 — controls whether screens may offer actions whose backing provider is mock-only (ACH/payment
 * submission, tax-filing approval/transmission, tax-deposit/remittance initiation).
 *
 * No live provider exists for any of them. Their backend providers (`MockAchProvider`,
 * `MockTaxFilingProvider`, `MockTaxRemittanceProvider`) answer "Accepted"/"Settled" without transmitting
 * anything, and a Production API process refuses to start with them. A production bundle therefore does
 * NOT offer the actions, and the screen says so — it must never show a filing "Accepted", a deposit
 * "Settled" or a batch "Submitted" when nothing left EPN.
 *
 * - Development / automated tests (`PROD` is false): enabled, the screens already carry
 *   "Sandbox Simulation Only" labelling.
 * - Any production bundle (`vite build`): disabled. There is deliberately NO environment switch that
 *   re-enables them — a bundle cannot know which backend it will be pointed at, so the only safe answer
 *   is "unavailable" until a real, approved provider exists and this module is deliberately changed.
 */
export interface CommercialActionsEnv {
  PROD: boolean;
}

export function sandboxTransmissionActionsAvailable(env: CommercialActionsEnv): boolean {
  return !env.PROD;
}

export const sandboxTransmissionActionsEnabled = sandboxTransmissionActionsAvailable(import.meta.env);

/**
 * EPN-105 — whether the UI may show MOCK billing affordances: the "mock sandbox" labels, the mock Checkout and
 * customer-portal pages, and the demo controls that simulate a failed payment or an expired trial. They exist only to
 * demonstrate and test the screens without a payment provider. A production bundle never shows them, whatever
 * `VITE_USE_MOCKS` says (a Production bundle also refuses to start with mocks — see `productionMockGuard`). As with
 * the actions above there is deliberately no environment switch.
 */
export function mockBillingAffordancesAvailable(env: CommercialActionsEnv, mocksSelected: boolean): boolean {
  return !env.PROD && mocksSelected;
}

export const mockBillingAffordancesEnabled = mockBillingAffordancesAvailable(import.meta.env, useMocks);

// EPN-99's `selfServicePlanChangeAvailable` flag was removed by EPN-105: customers now change plans through the billing
// center, which previews the exact charge first and is enforced entirely by the server (a missing provider answers 503
// `BILLING_UNAVAILABLE`, which the screens report honestly). Nothing in the UI decides plan-change eligibility.
